import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Flag to prevent redirect during initial token verification
let isVerifyingToken = false;
export const setVerifyingToken = (value) => { isVerifyingToken = value; };

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies for httpOnly token support
});

// Token storage - Use sessionStorage with localStorage fallback for migration
// Note: For production, tokens should be stored in httpOnly cookies set by backend
const tokenStorage = {
  get: () => {
    // Check sessionStorage first, then fallback to localStorage for migration
    let token = sessionStorage.getItem('auth_token');
    if (!token) {
      // Try legacy localStorage keys
      token = localStorage.getItem('token');
      if (token) {
        // Migrate to sessionStorage
        sessionStorage.setItem('auth_token', token);
        localStorage.removeItem('token');
      }
    }
    return token;
  },
  set: (token) => {
    sessionStorage.setItem('auth_token', token);
    // Clean up legacy localStorage
    localStorage.removeItem('token');
  },
  remove: () => {
    sessionStorage.removeItem('auth_token');
    // Clean up legacy localStorage keys
    localStorage.removeItem('token');
  }
};

const userStorage = {
  get: () => {
    // Check sessionStorage first, then fallback to localStorage for migration
    let data = sessionStorage.getItem('auth_user');
    if (!data) {
      // Try legacy localStorage key
      data = localStorage.getItem('user');
      if (data) {
        // Migrate to sessionStorage
        sessionStorage.setItem('auth_user', data);
        localStorage.removeItem('user');
      }
    }
    return data ? JSON.parse(data) : null;
  },
  set: (user) => {
    sessionStorage.setItem('auth_user', JSON.stringify(user));
    // Clean up legacy localStorage
    localStorage.removeItem('user');
  },
  remove: () => {
    sessionStorage.removeItem('auth_user');
    // Clean up legacy localStorage keys
    localStorage.removeItem('user');
  }
};

// Refresh token persists across tab/app restarts so the user is silently re-authenticated
const refreshStorage = {
  get: () => localStorage.getItem('auth_refresh'),
  set: (token) => localStorage.setItem('auth_refresh', token),
  remove: () => localStorage.removeItem('auth_refresh')
};

export { tokenStorage, userStorage, refreshStorage };

export const clearAuthStorage = () => {
  tokenStorage.remove();
  userStorage.remove();
  refreshStorage.remove();
};

// Single-flight refresh: concurrent 401s share one refresh request
let refreshPromise = null;
let lastRefreshFailure = null;
export const getLastRefreshFailure = () => lastRefreshFailure;
export const refreshAccessToken = async () => {
  const refreshToken = refreshStorage.get();
  if (!refreshToken) return null;
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE}/auth/refresh-token`, { refresh_token: refreshToken })
      .then((res) => {
        lastRefreshFailure = null;
        tokenStorage.set(res.data.token);
        return res.data.token;
      })
      .catch((err) => {
        // Only a definitive auth failure invalidates the refresh token; network/5xx errors keep it for retry
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          lastRefreshFailure = err.response?.data?.detail || 'expired';
          refreshStorage.remove();
        } else {
          lastRefreshFailure = 'network';
        }
        return null;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

export const noticeFromDetail = (detail) => (String(detail || '').toLowerCase().includes('revoked') ? 'revoked' : 'expired');

const AUTH_PATHS_NO_REFRESH = ['/auth/login', '/auth/register', '/auth/refresh-token', '/auth/logout', '/auth/google/session'];

export const SESSION_NOTICE_KEY = 'auth_notice';
export const setSessionNotice = (reason) => sessionStorage.setItem(SESSION_NOTICE_KEY, reason);
export const consumeSessionNotice = () => {
  const notice = sessionStorage.getItem(SESSION_NOTICE_KEY);
  sessionStorage.removeItem(SESSION_NOTICE_KEY);
  return notice;
};

const redirectToLogin = (detail) => {
  clearAuthStorage();
  setSessionNotice(noticeFromDetail(lastRefreshFailure || detail));
  const currentPath = window.location.pathname;
  if (!currentPath.startsWith('/login') && !currentPath.startsWith('/register') && currentPath !== '/') {
    window.location.href = '/login';
  }
};

// Add auth token to requests
api.interceptors.request.use(async (config) => {
  let token = tokenStorage.get();
  // Access token gone (tab/app reopened) but refresh token present: re-authenticate before the request
  if (!token && refreshStorage.get() && !AUTH_PATHS_NO_REFRESH.some((p) => (config.url || '').includes(p))) {
    token = await refreshAccessToken();
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401: try to refresh the access token once and retry; only logout if refresh fails
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    if (response?.status !== 401 || !config) return Promise.reject(error);

    const url = config.url || '';
    const skip = AUTH_PATHS_NO_REFRESH.some((p) => url.includes(p));
    if (!skip && !config._retried) {
      config._retried = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        config.headers.Authorization = `Bearer ${newToken}`;
        return api(config);
      }
      // Transient failure (offline/5xx) while a refresh token still exists: do not log the user out
      if (lastRefreshFailure === 'network' && refreshStorage.get()) return Promise.reject(error);
    }

    if (!isVerifyingToken && !skip) redirectToLogin(response?.data?.detail);
    return Promise.reject(error);
  }
);

// Auth
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);
export const logoutSession = (token) =>
  api.post('/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } });
export const getMe = () => api.get('/auth/me');
export const verifyEmail = (email, code) => api.post('/auth/verify-email', { email, code });
export const resendVerificationCode = (email) => api.post('/auth/resend-code', { email });
export const changePassword = (currentPassword, newPassword) => 
  api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (email, code, newPassword) => 
  api.post('/auth/reset-password', { email, code, new_password: newPassword });

// Platform Connection Health
export const getConnectionHealth = () => api.get('/platforms/connection-health');

// Clip Processor - Auto-cut videos
export const processVideoUrl = (data) => api.post('/generated-clips/process', data);
export const getClipJobStatus = (jobId) => api.get(`/generated-clips/job/${jobId}`);
export const getMyClips = (params = {}) => api.get('/generated-clips/my-clips', { params });
export const downloadClip = (clipId) => api.get(`/generated-clips/download/${clipId}`, { responseType: 'blob' });
export const getClipThumbnail = (clipId) => `${api.defaults.baseURL}/generated-clips/thumbnail/${clipId}`;
export const deleteGeneratedClip = (clipId) => api.delete(`/generated-clips/${clipId}`);
export const getYouTubeCookiesStatus = () => api.get('/generated-clips/youtube-cookies/status');
export const saveYouTubeCookies = (cookies) => api.post('/generated-clips/youtube-cookies', { cookies });

// CUA (Computer Use Agent) API Keys
export const createCuaApiKey = (data) => api.post('/cua/keys', data);
export const listCuaApiKeys = () => api.get('/cua/keys');
export const revokeCuaApiKey = (keyId) => api.delete(`/cua/keys/${keyId}`);

// Secure Vault - E2E Encrypted Credentials
export const createVault = () => api.post('/vault/create');
export const listVaultCredentials = () => api.get('/vault/list');
export const storeVaultCredential = (data) => api.post('/vault/store', data);
export const retrieveVaultCredential = (data) => api.post('/vault/retrieve', data);
export const deleteVaultCredential = (credentialId, masterPassword) => 
  api.delete(`/vault/${credentialId}?master_password=${encodeURIComponent(masterPassword)}`);
export const startE2ETransfer = (data) => api.post('/vault/transfer/start', data);
export const transferVaultCredentialPlatform = (credentialId, data) =>
  api.post(`/vault/${credentialId}/transfer-platform`, data);

// Admin (Developer Only)
export const getGuestList = () => api.get('/admin/guests');
export const createGuestAccess = (email, durationDays, plan, note) => 
  api.post('/admin/guests', { email, duration_days: durationDays, plan, note });
export const revokeGuestAccess = (guestId) => api.delete(`/admin/guests/${guestId}`);
export const getAdminStats = () => api.get('/admin/stats');

// Billing
export const getPlans = () => api.get('/billing/plans');
export const createCheckout = (planId, originUrl) => 
  api.post(`/billing/checkout?plan_id=${planId}&origin_url=${encodeURIComponent(originUrl)}`);
export const getCheckoutStatus = (sessionId) => api.get(`/billing/checkout/status/${sessionId}`);
export const getSubscription = () => api.get('/billing/subscription');
export const cancelSubscription = () => api.post('/billing/cancel');

// Affiliates
export const joinAffiliateProgram = () => api.post('/affiliates/join');
export const getAffiliateDashboard = () => api.get('/affiliates/dashboard');
export const updatePayoutSettings = (email, method) => 
  api.put(`/affiliates/payout-settings?payout_email=${email}&payout_method=${method}`);

// Social Accounts
export const getSocialAccounts = () => api.get('/social-accounts');
export const connectSocialAccount = (platform) => api.post(`/social-accounts/connect?platform=${platform}`);
export const disconnectSocialAccount = (id) => api.delete(`/social-accounts/${id}`);

// Podcasts
export const addPodcast = (youtubeUrl) => api.post(`/podcasts/add?youtube_url=${encodeURIComponent(youtubeUrl)}`);
export const analyzePodcast = (podcastId) => api.post(`/podcasts/${podcastId}/analyze`);

// Podcast Filters and Listing
export const getPodcastThemes = () => api.get('/podcasts/themes');
export const getPodcastLanguages = () => api.get('/podcasts/languages');
export const getPodcasts = (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.status) queryParams.append('status', params.status);
  if (params.theme) queryParams.append('theme', params.theme);
  if (params.language) queryParams.append('language', params.language);
  if (params.limit) queryParams.append('limit', params.limit || 50);
  const queryString = queryParams.toString();
  return api.get(`/podcasts${queryString ? `?${queryString}` : ''}`);
};

export const addPodcastForProcessing = (youtubeId) => 
  api.post(`/podcasts/add?youtube_url=${encodeURIComponent(`https://youtube.com/watch?v=${youtubeId}`)}`);

export const addPodcastByUrl = (youtubeUrl) => 
  api.post(`/podcasts/add?youtube_url=${encodeURIComponent(youtubeUrl)}`);

// Discovery via Desktop Agent (no YouTube API)
const youtubeIdFromUrl = (url = '') => {
  const m = url.match(/(?:v=|youtu\.be\/|shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : url;
};
export const toPodcastCard = (v) => {
  const id = v.video_id || youtubeIdFromUrl(v.url);
  return {
    youtube_id: id,
    url: v.url,
    title: v.title || 'Vídeo descoberto pelo Agente',
    channel_name: v.channel || v.channel_name || '',
    thumbnail_url: v.thumbnail || v.thumbnail_url || `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
    duration_seconds: v.duration || v.duration_seconds || 0,
    view_count: v.views || v.view_count || 0,
    like_count: v.likes || v.like_count || 0,
    comment_count: v.comments || v.comment_count || 0,
    trend_score: v.trend_score || 0,
    language: v.language,
    discovered_at: v.discovered_at,
    source: 'desktop_agent'
  };
};
export const discoverySearch = async (query, maxResults = 12, language = 'pt') => {
  const res = await api.post('/discovery/search', { query, max_results: maxResults, language });
  return { ...res.data, results: (res.data.results || []).map(toPodcastCard) };
};
export const getDiscoveryResults = async (query, limit = 24) => {
  const res = await api.get(`/discovery/results?limit=${limit}${query ? `&query=${encodeURIComponent(query)}` : ''}`);
  return { ...res.data, results: (res.data.results || []).map(toPodcastCard) };
};

// Segments
export const getSegments = (podcastId) => api.get(`/segments/${podcastId}`);

// Clips
export const getClips = (status, limit = 50) => api.get(`/clips${status ? `?status=${status}&` : '?'}limit=${limit}`);
export const getClip = (id) => api.get(`/clips/${id}`);
export const generateClip = (podcastId, segmentId, title, platforms) => 
  api.post(`/clips/generate?podcast_id=${podcastId}&segment_id=${segmentId}&title=${encodeURIComponent(title)}&platforms=${platforms.join(',')}`);
export const generateViralTitles = (clipId) => api.post(`/clips/${clipId}/generate-titles`);
export const deleteClip = (id) => api.delete(`/clips/${id}`);
export const scheduleClip = (clipId, platform, scheduledAt) => 
  api.post(`/clips/${clipId}/schedule?platform=${platform}${scheduledAt ? `&scheduled_at=${scheduledAt}` : ''}`);

// Backward compatibility aliases
export const schedulePublish = (data) => scheduleClip(data.clip_id, data.platform, data.scheduled_at);

// Publishing
export const getPublishJobs = (status) => api.get(`/publish-jobs${status ? `?status=${status}` : ''}`);
export const getPublishingQueue = (status) => getPublishJobs(status);
export const executePublish = (jobId) => api.post(`/publish-jobs/${jobId}/publish`);
export const cancelPublishJob = (jobId) => api.delete(`/publish-jobs/${jobId}`);
export const publishClipToMultiplePlatforms = (clipId, platforms, caption, hashtags) => 
  api.post('/platforms/publish', { clip_id: clipId, platforms, caption, hashtags });

// Social Platforms
export const getPlatformLimits = () => api.get('/platforms/limits');
export const getConnectedAccounts = () => api.get('/platforms/accounts');
export const disconnectAccount = (accountId) => api.delete(`/platforms/accounts/${accountId}`);
export const getPlatformAnalyticsReal = (platform) => api.get(`/platforms/analytics/${platform}`);

// Analytics
export const getAnalyticsOverview = () => api.get('/analytics/overview');
export const getClipAnalytics = (clipId) => api.get(`/analytics/clips/${clipId}`);
export const getAnalyticsTrends = (days = 30) => api.get(`/analytics/trends?days=${days}`);

// Platform analytics collected by the Desktop Agent (no platform APIs, no demo data)
export const getPlatformAnalytics = async (platform) => {
  const response = await api.get(`/platforms/analytics/${platform}`);
  return { data: { platform, ...(response.data.analytics || {}), awaiting_agent: response.data.awaiting_agent } };
};
export const getCollectionStatus = () => api.get('/analytics/collection-status');
export const requestAnalyticsRefresh = () => api.post('/analytics/request-refresh');

// Vault accounts: covers, labels, agent status checks
export const uploadAccountCover = (credentialId, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/vault/${credentialId}/cover`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const removeAccountCover = (credentialId) => api.delete(`/vault/${credentialId}/cover`);
export const updateAccountLabel = (credentialId, label) => {
  const form = new FormData();
  form.append('account_label', label);
  return api.patch(`/vault/${credentialId}`, form);
};
export const requestAccountCheck = (credentialId) => api.post(`/platforms/check-account/${credentialId}`);
// Private files are fetched with the Authorization header (never a token in the URL) and shown as blob URLs
export const fetchFileBlobUrl = async (path) => {
  const res = await api.get(`/files/${path}`, { responseType: 'blob' });
  return URL.createObjectURL(res.data);
};

export const getTopClips = (limit = 10, metric = 'views') => {
  return getClips(null, limit);
};

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Reports
export const getLatestReport = () => api.get('/reports/latest');
export const getReports = (limit = 10) => api.get(`/reports?limit=${limit}`);
export const generateReport = () => api.post('/reports/generate');

// Demo Data
export const seedDemoData = () => api.post('/seed-demo-data');

// Settings
export const getSettings = () => {
  return Promise.resolve({
    data: {
      clip_min_duration: 15,
      clip_max_duration: 60,
      clips_per_video: 5,
      auto_discover: true,
      auto_publish: false,
      virality_threshold: 0.7,
      subtitle_style: 'bold_animated',
      platforms: []
    }
  });
};

export const updateSettings = (data) => {
  return Promise.resolve({ data: { message: 'Settings updated' } });
};

export const updatePlatformSettings = (platform, settings) => {
  return Promise.resolve({ data: { message: `${platform} settings updated` } });
};

// AI Assistant
export const sendAIMessage = (message) => api.post('/ai/chat', { message });
export const transcribeAudio = (formData) => api.post('/ai/transcribe', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const getAIHistory = (limit = 50) => api.get(`/ai/history?limit=${limit}`);
export const clearAIHistory = () => api.delete('/ai/history');
export const getAISuggestions = () => api.get('/ai/suggestions');

// Agent Command Console (App → Desktop Agent)
export const getAgentCatalog = () => api.get('/agent/catalog');
export const getAgentConnection = () => api.get('/agent/connection');
export const sendAgentCommand = (message, session_id = 'default') => api.post('/agent/command', { message, session_id });
export const getAgentCommandHistory = (session_id = 'default') => api.get(`/agent/command/history?session_id=${session_id}`);
export const clearAgentCommandHistory = (session_id = 'default') => api.delete(`/agent/command/history?session_id=${session_id}`);
export const getAgentQueueTasks = () => api.get('/autopilot/agent-queue');
export const getAgentEndpoint = () => api.get('/agent/endpoint');
export const saveAgentEndpoint = (data) => api.post('/agent/endpoint', data);
export const deleteAgentEndpoint = () => api.delete('/agent/endpoint');
export const testAgentEndpoint = () => api.post('/agent/endpoint/test');

// Video Enhancement
export const getVideoQualityPresets = () => api.get('/video/quality-presets');
export const analyzeVideoQuality = (videoUrl) => api.post('/video/analyze', null, { params: { video_url: videoUrl } });
export const enhanceVideo = (clipId, platform = 'youtube_shorts', preset = 'auto') => 
  api.post('/video/enhance', { clip_id: clipId, platform, enhancement_preset: preset });
export const getEnhancementStatus = (jobId) => api.get(`/video/enhance/${jobId}/status`);
export const getEnhancementJobs = (status = null) => 
  api.get(`/video/enhance/jobs${status ? `?status=${status}` : ''}`);
export const getVideoSettings = () => api.get('/video/settings');
export const updateVideoSettings = (settings) => api.put('/video/settings', settings);

// Google OAuth
export const exchangeGoogleSession = (sessionId) => 
  api.post('/auth/google/session', { session_id: sessionId });

// Generic HTTP methods for new endpoints
export const get = (url) => api.get(url.replace('/api', ''));
export const post = (url, data) => api.post(url.replace('/api', ''), data);
export const put = (url, data) => api.put(url.replace('/api', ''), data);
export const del = (url) => api.delete(url.replace('/api', ''));
// Alias for delete (reserved word)
export { del as delete };

export default api;
